import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { FiltersService } from '../../../permissions/services/filters.service';
import { Entity, Paginate, Permission } from '../../../permissions/definitions/permissions.models';
import { MasterDataEntity } from '../../../shared/enums';
import { CreateReportRequestBody, CreateReportRequestResponse, ReportRequestsResponse, ReportTypeDto } from '../shared/interfaces';
import { StoreAuxService } from '../../../services/storeAux/store-aux.service';
import { environment } from '../../../../environments/environment';
import { SearchCriteria } from '../../../permissions/definitions/permissions.enums';

const MODULE = 'permissions';
const PAGE = 1;
const LIMIT = 200;

@Injectable({ providedIn: 'root' })
export class ReportAutomationService {
  private readonly URL_MAKERS_REPORTS_MGR = `${environment.urlGatewayInt}/makers-reports-mgr`;

  constructor(
    private readonly _filters: FiltersService,
    private readonly _http: HttpClient,
    private readonly _storeAuxService: StoreAuxService
  ) {}

  getReportRequests(userEmail: string, page: number, limit: number): Observable<ReportRequestsResponse> {
    const params = { user_email: userEmail, page: String(page), limit: String(limit) };
    return this._http
      .get<ReportRequestsResponse>(`${this._getBaseUrl()}/report-requests`, { params })
      .pipe(
        catchError(() => of({ page, limit, total_rows: 0, total_pages: 0, rows: [] }))
      );
  }

  createReportRequest(body: CreateReportRequestBody): Observable<CreateReportRequestResponse> {
    return this._http.post<CreateReportRequestResponse>(`${this._getBaseUrl()}/report-requests`, body);
  }

  getReportTypes(): Observable<ReportTypeDto[]> {
    return this._http
      .get<{ report_types: ReportTypeDto[] }>(`${this._getBaseUrl()}/report-types`)
      .pipe(
        map(res => res.report_types || []),
        catchError(() => of([]))
      );
  }

  getMakers(): Observable<Entity[]> {
    return this._filters
      .getEntities(
        this._filters.getBody(MODULE, MasterDataEntity.maker),
        this._filters.getParams(PAGE, LIMIT, '')
      )
      .pipe(
        map(({ data: { records } }: Permission<Paginate<Entity>>) => records || []),
        catchError(() => of([]))
      );
  }

  getTrademarks(makerId: number): Observable<Entity[]> {
    const dependency = [{ name: MasterDataEntity.maker, data: [makerId] }];
    return this._filters
      .getEntities(
        this._filters.getBody(MODULE, MasterDataEntity.trademark, dependency),
        this._filters.getParams(PAGE, LIMIT, '')
      )
      .pipe(
        map(({ data: { records } }: Permission<Paginate<Entity>>) => records || []),
        catchError(() => of([]))
      );
  }

  getVerticals(): Observable<Entity[]> {
    return this._filters
      .getEntities(
        this._filters.getBody(MODULE, MasterDataEntity.vertical),
        this._filters.getParams(PAGE, LIMIT, '')
      )
      .pipe(
        map(({ data: { records } }: Permission<Paginate<Entity>>) => records || []),
        catchError(() => of([]))
      );
  }

  getStoreBrands(page: number, limit: number, search: string): Observable<Entity[]> {
    const params = this._filters.getParams(page, limit, search, SearchCriteria.name);
    const body = this._filters.getBody(MODULE, MasterDataEntity.store_brand);
    return this._filters
      .getStores(body, params)
      .pipe(
        map(({ data: { records } }: Permission<Paginate<Entity>>) => records || []),
        catchError(() => of([]))
      );
  }

  getSubcategories(makerId?: number, trademarkId?: number): Observable<Entity[]> {
    const dependency = makerId != null && trademarkId != null
      ? [
        { name: MasterDataEntity.maker, data: [makerId] },
        { name: MasterDataEntity.trademark, data: [trademarkId] }
      ]
      : undefined;
    return this._filters
      .getEntities(
        this._filters.getBody(MODULE, MasterDataEntity.subcategory, dependency),
        this._filters.getParams(PAGE, LIMIT, '')
      )
      .pipe(
        map(({ data: { records } }: Permission<Paginate<Entity>>) => records || []),
        catchError(() => of([]))
      );
  }

  getSegments(makerId?: number, trademarkId?: number, subcategoryId?: number): Observable<Entity[]> {
    const dependency = this._makerTrademarkSubcategoryDependency(makerId, trademarkId, subcategoryId);
    return this._filters
      .getEntities(
        this._filters.getBody(MODULE, MasterDataEntity.segment, dependency),
        this._filters.getParams(PAGE, LIMIT, '')
      )
      .pipe(
        map(({ data: { records } }: Permission<Paginate<Entity>>) => records || []),
        catchError(() => of([]))
      );
  }

  private _makerTrademarkSubcategoryDependency(
    makerId?: number,
    trademarkId?: number,
    subcategoryId?: number
  ): { name: string; data: number[] }[] | undefined {
    return makerId != null && trademarkId != null && subcategoryId != null
      ? [
        { name: MasterDataEntity.maker, data: [makerId] },
        { name: MasterDataEntity.trademark, data: [trademarkId] },
        { name: MasterDataEntity.subcategory, data: [subcategoryId] }
      ]
      : undefined;
  }

  private _getBaseUrl(): string {
    return this._storeAuxService.getCountry(this.URL_MAKERS_REPORTS_MGR);
  }
}
